import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime, formatCurrency, laneLabel, prettify } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import EntityForm from "@/components/EntityForm";
import DetailDrawer from "@/components/DetailDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PopoutDialog } from "@/components/ui/PopoutDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Search, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function ResourcePage({ config }) {
  const { user, activeBranch } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canWrite = Array.isArray(user?.writable_resources)
    ? user.writable_resources.includes(config.key)
    : false;

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1" && canWrite) {
      setEditing(null);
      setFormOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, canWrite]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      if (activeBranch) params.branch_id = activeBranch;
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const { data } = await api.get(`/${config.key}`, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Could not load " + config.title);
    } finally {
      setLoading(false);
    }
  }, [config.key, config.title, q, filters, activeBranch]);

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (item) => { setEditing(item); setFormOpen(true); setDrawerOpen(false); };
  const openDetail = (item) => { setSelected(item); setDrawerOpen(true); };

  const submit = async (payload) => {
    setSubmitting(true);
    try {
      if (editing) {
        await api.put(`/${config.key}/${editing.id}`, payload);
        toast.success(`${config.singular} updated`);
      } else {
        await api.post(`/${config.key}`, payload);
        toast.success(`${config.singular} created`);
      }
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/${config.key}/${deleteTarget.id}`);
      toast.success(`${config.singular} removed`);
      setDeleteTarget(null);
      setDrawerOpen(false);
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const convert = async (lead) => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/convert`, {});
      toast.success(`Converted to case ${data.case.case_number}`);
      setDrawerOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    }
  };

  const renderCell = (col, item) => {
    const v = item[col.key];
    if (col.type === "badge") return <StatusBadge value={v} testid={`cell-${col.key}`} />;
    if (col.type === "lane") return <span className="text-sm">{laneLabel(v)}</span>;
    if (col.type === "date") return <span className="text-sm text-muted-foreground">{formatDate(v)}</span>;
    if (col.type === "datetime") return <span className="text-sm text-muted-foreground">{formatDateTime(v)}</span>;
    if (col.type === "currency") return <span className="text-sm tabular-nums">{v ? formatCurrency(v) : "—"}</span>;
    if (v === undefined || v === null || v === "") return <span className="text-muted-foreground">—</span>;
    return <span className={`text-sm ${col.primary ? "font-semibold text-[#252221]" : "text-[#252221]"}`}>{String(v)}</span>;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221]">{config.title}</h2>
          {config.subtitle && <p className="text-sm text-muted-foreground mt-0.5">{config.subtitle}</p>}
        </div>
        {canWrite && (
          <Button onClick={openNew} data-testid={`new-${config.key}-button`} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
            <Plus size={17} className="mr-1.5" /> New {config.singular}
          </Button>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2.5 mb-4 rounded-xl border border-[#E6EDF2] bg-white p-2.5" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${config.title.toLowerCase()}...`}
            className="pl-9 h-10 border-0 bg-[#F7FAFC]" data-testid="resource-search-input" />
        </div>
        {(config.filters || []).map((f) => (
          <Select key={f.key} value={filters[f.key] || "all"} onValueChange={(v) => setFilters((p) => ({ ...p, [f.key]: v }))}>
            <SelectTrigger className="h-10 w-[170px]" data-testid={`filter-${f.key}`}>
              <SelectValue placeholder={f.label} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All {f.label}</SelectItem>
              {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
        <span className="text-xs text-muted-foreground ml-auto px-2">{total} {total === 1 ? "record" : "records"}</span>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        {loading ? (
          <div className="p-4 space-y-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(155,185,206,0.18)" }}>
              <Inbox size={26} className="text-[#1C4358]" />
            </div>
            <p className="font-medium text-[#252221]">No {config.title.toLowerCase()} yet</p>
            <p className="text-sm text-muted-foreground mb-4">Get started by adding your first {config.singular.toLowerCase()}.</p>
            {canWrite && (
              <Button onClick={openNew} variant="outline" data-testid={`empty-new-${config.key}-button`}>
                <Plus size={16} className="mr-1.5" /> New {config.singular}
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto mmm-scroll">
            <Table data-testid={`${config.key}-table`}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {config.columns.map((c) => (
                    <TableHead key={c.key} className="text-xs font-semibold text-[#1C4358]/80 whitespace-nowrap">{c.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} onClick={() => openDetail(item)}
                    className="cursor-pointer hover:bg-[rgba(155,185,206,0.10)]" data-testid={`${config.key}-row`}>
                    {config.columns.map((c) => (
                      <TableCell key={c.key} className="py-3">{renderCell(c, item)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Create / Edit form — modal by default, detachable to a floating pop-out */}
      <PopoutDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        title={editing ? `Edit ${config.singular}` : `New ${config.singular}`}
        subtitle={config.title}
        testid="entity-form-dialog"
      >
        <EntityForm config={config} initial={editing} onSubmit={submit} onCancel={() => setFormOpen(false)} submitting={submitting} />
      </PopoutDialog>

      {/* Detail drawer */}
      <DetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        config={config}
        item={selected}
        onEdit={openEdit}
        onDelete={(item) => setDeleteTarget(item)}
        onConvert={convert}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this {config.singular.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>This will archive the record. You can ask an admin to restore it if needed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-button" className="text-white" style={{ backgroundColor: "#7A0F12" }}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
